import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import { toast } from "sonner";
import { fireCelebration } from "../lib/celebrate";
import { Lightning, Fire, Flask, Trophy, Star } from "@phosphor-icons/react";

const STREAK_MILESTONES = [100, 30, 7];

/**
 * Personal stats-at-a-glance strip for the logged-in hacker home.
 * Card-less: raised gradient surface + accent top-edge, no outline boxes.
 * Pulls /me/xp, /me/streak, /me/labs/solved-count, /users/me/rank in parallel.
 */
export default function HackerStatsBar() {
    const { t } = useTranslation();
    const [xp, setXp] = useState(null);
    const [streak, setStreak] = useState(null);
    const [labs, setLabs] = useState(null);
    const [rank, setRank] = useState(null);

    useEffect(() => {
        let alive = true;
        Promise.allSettled([
            api.get("/me/xp"),
            api.get("/me/streak"),
            api.get("/me/labs/solved-count"),
            api.get("/users/me/rank"),
        ]).then(([x, s, l, r]) => {
            if (!alive) return;
            if (x.status === "fulfilled") setXp(x.value.data);
            if (s.status === "fulfilled") {
                setStreak(s.value.data);
                const cur = s.value.data?.current_streak || 0;
                const m = STREAK_MILESTONES.find((v) => cur >= v);
                if (m && !localStorage.getItem(`hc_streak_celebrated_${m}`)) {
                    localStorage.setItem(`hc_streak_celebrated_${m}`, new Date().toISOString());
                    fireCelebration(["#f43f5e", "#fbbf24", "#fb923c", "#e2e8f0"]);
                    toast.success(`🔥 ${m}-day streak — you're on fire!`, { duration: 6000 });
                }
            }
            if (l.status === "fulfilled") setLabs(l.value.data);
            if (r.status === "fulfilled") setRank(r.value.data);
        });
        return () => { alive = false; };
    }, []);

    const level = xp?.level ?? "—";
    const pct = Math.max(0, Math.min(100, xp?.progress_pct ?? 0));
    const stats = [
        { key: "karma", icon: Star, accent: "amber", value: rank?.karma ?? "—", label: t("stats.karma"),
          sub: rank?.rank ? t("stats.rank_n", { n: rank.rank }) : t("stats.unranked"), to: "/leaderboard" },
        { key: "streak", icon: Fire, accent: "rose", value: streak?.current_streak ?? "—", label: t("stats.day_streak"),
          sub: streak?.longest_streak ? t("stats.best_n", { n: streak.longest_streak }) : t("stats.start_today"), to: "/quests" },
        { key: "labs", icon: Flask, accent: "emerald", value: labs?.count ?? "—", label: t("stats.labs_solved"),
          sub: t("stats.keep_popping"), to: "/labs" },
        { key: "rank", icon: Trophy, accent: "violet", value: rank?.rank ? `#${rank.rank}` : "—", label: t("stats.global_rank"),
          sub: rank?.total ? t("stats.of_n", { n: rank.total.toLocaleString() }) : t("stats.climb_up"), to: "/leaderboard" },
    ];
    const ACCENT = {
        amber: "text-amber-300", rose: "text-rose-300", emerald: "text-emerald-300",
        violet: "text-violet-300", cyan: "text-cyan-300",
    };
    const RGB = {
        amber: "251,191,36", rose: "244,63,94", emerald: "16,185,129",
        violet: "167,139,250", cyan: "34,211,238",
    };

    return (
        <div className="relative overflow-hidden rounded-sm hc-statsband" data-testid="hacker-stats-bar">
            <div className="pointer-events-none absolute inset-x-0 top-0 h-px opacity-70"
                 style={{ background: "linear-gradient(90deg, transparent, rgba(34,211,238,0.5), rgba(167,139,250,0.4), transparent)" }} aria-hidden="true" />
            <div className="pointer-events-none absolute -top-16 -right-14 w-56 h-56 rounded-full bg-cyan-500/15 blur-[80px]" aria-hidden="true" />

            <div className="relative p-4 sm:p-5">
                {/* Level + XP progress */}
                <div className="flex items-center justify-between gap-4 mb-3">
                    <div className="flex items-center gap-2.5 min-w-0">
                        <span className="shrink-0 w-9 h-9 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                            <Lightning size={18} weight="fill" className="text-cyan-300" />
                        </span>
                        <div className="min-w-0">
                            <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500">{t("stats.level")}</div>
                            <div className="text-lg font-black text-white leading-none" style={{ fontFamily: "'Space Grotesk', 'Chivo', sans-serif" }} data-testid="stats-level">
                                {level}
                            </div>
                        </div>
                    </div>
                    <div className="flex-1 min-w-0 max-w-xs">
                        <div className="flex items-center justify-between text-[10px] font-mono text-slate-500 mb-1">
                            <span>{(xp?.xp_total ?? 0).toLocaleString()} XP</span>
                            <span>{t("stats.pct_to_next", { pct })}</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                            <div className="h-full rounded-full transition-[width] duration-700"
                                 style={{ width: `${pct}%`, background: "linear-gradient(90deg, #06b6d4, #a78bfa)" }}
                                 data-testid="stats-xp-bar" />
                        </div>
                    </div>
                </div>

                {/* Stat cells */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                    {stats.map((s) => {
                        const Icon = s.icon;
                        return (
                            <Link key={s.key} to={s.to} data-testid={`stat-${s.key}`} aria-label={`${s.label}: ${s.value}`}
                                  className="group relative rounded-sm p-2.5 sm:p-3 transition-colors hc-statcell flex items-center gap-2.5 sm:block">
                                <div className="pointer-events-none absolute inset-x-3 top-0 h-px opacity-0 group-hover:opacity-100 transition-opacity"
                                     style={{ background: `linear-gradient(90deg, transparent, rgba(${RGB[s.accent]},0.6), transparent)` }} aria-hidden="true" />
                                <Icon size={16} className={`${ACCENT[s.accent]} shrink-0 sm:mb-1.5`} weight="duotone" />
                                <div className="min-w-0">
                                    <div className="text-lg sm:text-xl font-black text-white leading-none" style={{ fontFamily: "'Space Grotesk', 'Chivo', sans-serif" }}>{s.value}</div>
                                    <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 mt-1 truncate">{s.label}</div>
                                    <div className={`hidden sm:block text-[10px] font-mono mt-0.5 ${ACCENT[s.accent]} opacity-80`}>{s.sub}</div>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
